/**
 * @file SSOImportVendorDropdown.js
 * @description A dropdown component for selecting SSO vendors.
 */

import React, { useState, useEffect } from 'react';
import { fetchVendors } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const SSOImportVendorDropdown = ({
    className,
    inlineLabel = true,
    selectedVendor,
    onVendorChange,
    label = 'Vendor',
    placeholder = 'Select Vendor',
    isRequired = false,
    allowNullSelection = false,
}) => {
    const [vendors, setVendors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getVendors = async () => {
            setIsLoading(true);
            try {
                const allVendors = await fetchVendors();
                const allVendorsSorted = [...allVendors].sort((a, b) =>
                    a.name.localeCompare(b.name),
                );
                setVendors(allVendorsSorted);
            } catch (error) {
                console.error('Could not fetch vendors:', error);
                Toast.error(getText('msg_ef492d093e38'));
            } finally {
                setIsLoading(false);
            }
        };

        getVendors();
    }, []);

    const getDefaultLabel = () => {
        const matchingVendor = vendors.find(
            vendor => vendor.id === selectedVendor,
        );
        return matchingVendor ? matchingVendor.name : placeholder;
    };

    const vendorOptions = allowNullSelection
        ? [
              { value: null, label: placeholder },
              ...vendors.map(vendor => ({
                  value: vendor.id,
                  label: vendor.name,
              })),
          ]
        : vendors.map(vendor => ({
              value: vendor.id,
              label: vendor.name,
          }));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="vendor-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="vendor-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={vendorOptions}
                    value={selectedVendor}
                    onChange={onVendorChange}
                    disabled={vendors.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default SSOImportVendorDropdown;
